import { keepPreviousData, useQuery } from "@tanstack/react-query";
import collectionsService from "src/queries/services/collections";
import { fetchConfigReview } from "src/queries/services/configReview";
import { FETCH_QUERY_KEYS } from "../consts";

// A spec or listing that fails to load leaves its part of the review less detailed; it never fails the review.
const settle = (request) => request.catch(() => undefined);

// The review plus what it needs to be shown, as one result:
// - `specs`: each edited instance's collection (spec + groupings), keyed by "<collection>/<key>";
// - `listings`: each changed collection's listing ({ specRef, configRef, draftConfigRef }), keyed by collection.
// `contextOf(review)` names them: { instances: [{ collection, route, specKey, key }], collections: [{ collection, route }] }.
// Fetched together, so switching base keeps the previous review whole on screen until the next one is complete.
// They are fetched apart from useFetchCollection(s), whose store side effects belong to the collection pages.
const fetchReviewWithContext = async ({ assistantId, base, contextOf, signal }) => {
  const review = await fetchConfigReview(assistantId, base, { signal });
  const { instances = [], collections = [] } = contextOf(review);
  const [specs, listings] = await Promise.all([
    Promise.all(instances.map(async ({ collection, route, specKey, key }) => [
      `${collection}/${key}`,
      await settle(collectionsService.fetchCollection({ selectedAssistantId: assistantId, collectionName: route, collectionKey: specKey, collectionInstanceKey: key }))
    ])),
    Promise.all(collections.map(async ({ collection, route }) => [
      collection,
      await settle(collectionsService.fetchCollections({ selectedAssistantId: assistantId, collectionName: route }))
    ]))
  ]);
  return { review, specs: Object.fromEntries(specs), listings: Object.fromEntries(listings) };
};

export const useFetchConfigReview = (assistantId, base, { enabled = true, contextOf }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.CONFIG_REVIEW, assistantId, base],
  queryFn: ({ signal }) => fetchReviewWithContext({ assistantId, base, contextOf, signal }),
  enabled: enabled && !!assistantId,
  // Each open of the dialog must show the draft as it is now: the review is dropped as soon as the dialog
  // closes (it unmounts), so a reopen never starts from an old diff.
  staleTime: 0,
  gcTime: 0,
  // Switching between Last build and Live keeps the current review on screen until the other one arrives.
  placeholderData: keepPreviousData,
  retry: false
});
